import {
  checklistsApi,
  expressionDefinitionApi,
  subCompanyChecklistsAuthoringApi,
} from 'app/services/api';
import { normalize, schema } from 'normalizr';

import {
  CHECKLIST_READ_PARAMS_BY_DEPTH,
  ChecklistEntityType,
  ChecklistReadDepth,
  ChecklistTargetType,
} from 'constants/checklists';
import { catchAsyncActionError } from 'utils/action';
import { resolveChecklistReadDepth } from 'utils/checklist';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { selectMasterCompanyId } from 'store/auth/selectors';
import {
  selectAreChecklistsSchemasLoaded,
  selectHasChecklistNodeSeat,
} from 'store/system/selectors';

export const CHECKLISTS_ACTION_TYPE = createNamedActions('checklists', {
  SET_CHECKLIST_TARGET_TYPE: 'SET_CHECKLIST_TARGET_TYPE',
  GET_CHECKLISTS: 'GET_CHECKLISTS',
  GET_COMPLETENESS_CHECKLIST_OPTIONS: 'GET_COMPLETENESS_CHECKLIST_OPTIONS',
  GET_CHECKLIST: 'GET_CHECKLIST',
  GET_CHECKLIST_NODE: 'GET_CHECKLIST_NODE',
  GET_CHECKLISTS_SCHEMA: 'GET_CHECKLISTS_SCHEMA',
  GET_CHECKLISTS_VISIBILITY_SCHEMA: 'GET_CHECKLISTS_VISIBILITY_SCHEMA',
  CREATE_CHECKLIST: 'CREATE_CHECKLIST',
  CREATE_CHECKLIST_CATEGORY: 'CREATE_CHECKLIST_CATEGORY',
  CREATE_CHECKLIST_ITEM: 'CREATE_CHECKLIST_ITEM',
  EDIT_CHECKLIST: 'EDIT_CHECKLIST',
  REORDER_CHECKLIST: 'REORDER_CHECKLIST',
  EDIT_CHECKLIST_CATEGORY: 'EDIT_CHECKLIST_CATEGORY',
  EDIT_CHECKLIST_ITEM: 'EDIT_CHECKLIST_ITEM',
  DELETE_CHECKLIST: 'DELETE_CHECKLIST',
  DELETE_CHECKLIST_CATEGORY: 'DELETE_CHECKLIST_CATEGORY',
  DELETE_CHECKLIST_ITEM: 'DELETE_CHECKLIST_ITEM',
  DISABLE_GLOBAL_CHECKLIST: 'DISABLE_GLOBAL_CHECKLIST',
  ENABLE_GLOBAL_CHECKLIST: 'ENABLE_GLOBAL_CHECKLIST',
  CLONE_GLOBAL_CHECKLIST: 'CLONE_GLOBAL_CHECKLIST',
  DISABLE_GLOBAL_CHECKLIST_NODE: 'DISABLE_GLOBAL_CHECKLIST_NODE',
  ENABLE_GLOBAL_CHECKLIST_NODE: 'ENABLE_GLOBAL_CHECKLIST_NODE',
  SET_VISIBILITY_OVERRIDE: 'SET_VISIBILITY_OVERRIDE',
  REMOVE_VISIBILITY_OVERRIDE: 'REMOVE_VISIBILITY_OVERRIDE',
});

const checklistsSchema = new schema.Entity('data', {}, { idAttribute: 'id' });

const selectActiveChecklistTargetType = state =>
  state.system?.checklistsTargetType || ChecklistTargetType.LOAN;

const resolveChecklistsApi = targetType =>
  targetType === ChecklistTargetType.COMPANY ? subCompanyChecklistsAuthoringApi : checklistsApi;

const resolveSchemaApi = targetType =>
  targetType === ChecklistTargetType.COMPANY
    ? subCompanyChecklistsAuthoringApi
    : expressionDefinitionApi;

export const setChecklistTargetTypeAction = targetType => dispatch =>
  dispatch({
    type: CHECKLISTS_ACTION_TYPE.SET_CHECKLIST_TARGET_TYPE,
    payload: targetType,
  });

// The actions whose payload is a FULL checklist entity. Both the checklists store and the hydration marker
// branch on this, so a new mutation action only has to be added here.
export const CHECKLIST_FULL_ENTITY_ACTION_TYPES = new Set([
  CHECKLISTS_ACTION_TYPE.GET_CHECKLIST,
  CHECKLISTS_ACTION_TYPE.CREATE_CHECKLIST,
  CHECKLISTS_ACTION_TYPE.EDIT_CHECKLIST,
  CHECKLISTS_ACTION_TYPE.CREATE_CHECKLIST_CATEGORY,
  CHECKLISTS_ACTION_TYPE.EDIT_CHECKLIST_CATEGORY,
  CHECKLISTS_ACTION_TYPE.CREATE_CHECKLIST_ITEM,
  CHECKLISTS_ACTION_TYPE.EDIT_CHECKLIST_ITEM,
  CHECKLISTS_ACTION_TYPE.REORDER_CHECKLIST,
]);

export const getChecklistsAction = catchAsyncActionError(params => async (dispatch, getState) => {
  const {
    includeItems = false,
    includeCategories = false,
    includeExpressions = true,
  } = params || {};
  const requestedTargetType = selectActiveChecklistTargetType(getState());
  const api = resolveChecklistsApi(requestedTargetType);

  const { data } = await api.getChecklists({
    includeItems,
    includeCategories,
    includeExpressions,
    skipForbiddenError: true,
  });

  // Same race the single read guards: this response replaces the whole slice, so landing it after a
  // target-type switch leaves the tree showing one scope while every save routes through the other.
  if (selectActiveChecklistTargetType(getState()) !== requestedTargetType) {
    return null;
  }

  return dispatch({
    type: CHECKLISTS_ACTION_TYPE.GET_CHECKLISTS,
    payload: normalize(data, [checklistsSchema]).entities.data,
    meta: {
      includeExpressions,
      readDepth: resolveChecklistReadDepth({ includeItems, includeCategories }),
    },
  });
});

// Options for the company-settings "Completeness Checklist" picker. A completeness checklist is a
// HIDDEN decisioning engine (DEV-4603), so unlike the general checklists list (visible-only) this
// searches with `includeHidden` so the hidden completeness checklists — the global master and any
// company-specific ones — are selectable + displayable. Kept in its own slice so the plain
// checklists list (and the checklists-management page) stays hidden-free.
export const getCompletenessChecklistOptionsAction = catchAsyncActionError(() => async dispatch => {
  const { data } = await checklistsApi.searchChecklists({
    includeItems: false,
    includeCategories: false,
    includeGlobal: true,
    includeHidden: true,
    skipForbiddenError: true,
  });

  return dispatch({
    type: CHECKLISTS_ACTION_TYPE.GET_COMPLETENESS_CHECKLIST_OPTIONS,
    payload: data,
  });
});

export const getChecklistAction = catchAsyncActionError(
  ({ readDepth = ChecklistReadDepth.SELF, ...params } = {}) =>
    async (dispatch, getState) => {
      const requestedTargetType = selectActiveChecklistTargetType(getState());
      const api = resolveChecklistsApi(requestedTargetType);

      // The request shape and the depth recorded for it come from the same place, and the depth wins:
      // a caller cannot widen the request past what gets recorded and have a shallow read marked as
      // carrying everything. Defaults to the shallowest read for the same reason.
      const { data } = await api.getChecklist({
        ...params,
        ...CHECKLIST_READ_PARAMS_BY_DEPTH[readDepth],
      });

      // Switching target type replaces the whole checklists slice. Merging a read that started under the
      // previous scope would re-insert an out-of-scope checklist AND mark it hydrated, so nothing would
      // re-fetch it and a save from that row would go out through the other scope's API.
      if (selectActiveChecklistTargetType(getState()) !== requestedTargetType) {
        return null;
      }

      return dispatch({
        type: CHECKLISTS_ACTION_TYPE.GET_CHECKLIST,
        payload: data,
        meta: { readDepth },
      });
    },
);

const getChecklistNodeAction = catchAsyncActionError(
  ({ checklistId, categoryId, itemId }) =>
    async (dispatch, getState) => {
      const requestedTargetType = selectActiveChecklistTargetType(getState());
      const api = resolveChecklistsApi(requestedTargetType);

      const { data } = itemId
        ? await api.getChecklistItem({ checklistId, itemId })
        : await api.getChecklistCategory({ checklistId, categoryId });

      // Same reason the checklist read bails: a target-type switch replaces the slice, and merging a node
      // read that started under the previous scope would re-insert an out-of-scope node AND mark it
      // hydrated, so a save from that row would go out through the other scope's API.
      if (selectActiveChecklistTargetType(getState()) !== requestedTargetType) {
        return null;
      }

      // No seat means the tree is stale for this branch, which a checklist-wide read fixes and a second
      // node read would not.
      const nodeSeat = {
        checklistId,
        categoryId: itemId ? data.categoryId : data.id,
        itemId: itemId && data.id,
      };

      if (!selectHasChecklistNodeSeat(getState(), nodeSeat)) {
        // silent because this read is already inside catchAsyncActionError: without it a failure toasts
        // once from the inner wrapper and again from the outer one, for a single failed read.
        return dispatch(
          getChecklistAction({
            checklistId,
            silent: true,
            readDepth: itemId ? ChecklistReadDepth.ITEMS : ChecklistReadDepth.CATEGORIES,
          }),
        );
      }

      return dispatch({
        type: CHECKLISTS_ACTION_TYPE.GET_CHECKLIST_NODE,
        payload: data,
        meta: { checklistId, categoryId, itemId },
      });
    },
);

const inFlightHydrations = new Map();

const resolveNodeRead = ({ checklistId, entityType, entityId }) => {
  if (entityType === ChecklistEntityType.CHECKLIST) {
    return getChecklistAction({ checklistId, readDepth: ChecklistReadDepth.SELF });
  }

  return getChecklistNodeAction({
    checklistId,
    ...(entityType === ChecklistEntityType.CATEGORY
      ? { categoryId: entityId }
      : { itemId: entityId }),
  });
};

export const hydrateChecklistEntityAction =
  ({ checklistId, entityType, entityId, force = false }) =>
  (dispatch, getState) => {
    const key = [
      selectMasterCompanyId(getState()),
      selectActiveChecklistTargetType(getState()),
      checklistId,
      entityId,
    ].join(':');
    const inFlight = inFlightHydrations.get(key);

    if (inFlight && !force) {
      return inFlight;
    }

    const hydration = dispatch(resolveNodeRead({ checklistId, entityType, entityId })).finally(
      () => {
        if (inFlightHydrations.get(key) === hydration) {
          inFlightHydrations.delete(key);
        }
      },
    );

    inFlightHydrations.set(key, hydration);

    return hydration;
  };

// The expression catalog is static per company between deploys. The guard lives here rather than at the call
// site to keep it out of the page's effect dependencies, where a mid-load flag flip would re-run the effect
// and re-fetch the whole checklist tree.
export const getChecklistsSchemaAction = catchAsyncActionError(() => async (dispatch, getState) => {
  if (selectAreChecklistsSchemasLoaded(getState())) {
    return null;
  }

  const targetType = selectActiveChecklistTargetType(getState());
  const companyId = selectMasterCompanyId(getState());
  const { data } = await resolveSchemaApi(targetType).getChecklistsExpressionDefinition();

  return dispatch({
    type: CHECKLISTS_ACTION_TYPE.GET_CHECKLISTS_SCHEMA,
    payload: data,
    meta: { targetType, companyId },
  });
});

export const getChecklistsVisibilitySchemaAction = catchAsyncActionError(
  () => async (dispatch, getState) => {
    if (selectAreChecklistsSchemasLoaded(getState())) {
      return null;
    }

    const targetType = selectActiveChecklistTargetType(getState());
    const companyId = selectMasterCompanyId(getState());
    const { data } = await resolveSchemaApi(targetType).getChecklistsVisibilityDefinition();

    return dispatch({
      type: CHECKLISTS_ACTION_TYPE.GET_CHECKLISTS_VISIBILITY_SCHEMA,
      payload: data,
      meta: { targetType, companyId },
    });
  },
);

export const createChecklistAction = catchAsyncActionError(params => async (dispatch, getState) => {
  const api = resolveChecklistsApi(selectActiveChecklistTargetType(getState()));

  const { data } = await api.createChecklist(params);

  return dispatch({
    type: CHECKLISTS_ACTION_TYPE.CREATE_CHECKLIST,
    payload: data,
  });
});

export const editChecklistAction = catchAsyncActionError(params => async (dispatch, getState) => {
  const api = resolveChecklistsApi(selectActiveChecklistTargetType(getState()));

  const { data } = await api.editChecklist(params);

  return dispatch({
    type: CHECKLISTS_ACTION_TYPE.EDIT_CHECKLIST,
    payload: data,
  });
});

export const deleteChecklistAction = catchAsyncActionError(params => async (dispatch, getState) => {
  const api = resolveChecklistsApi(selectActiveChecklistTargetType(getState()));

  await api.deleteChecklist(params);

  return dispatch({
    type: CHECKLISTS_ACTION_TYPE.DELETE_CHECKLIST,
    meta: params,
  });
});

export const reorderChecklistAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const api = resolveChecklistsApi(selectActiveChecklistTargetType(getState()));

    const { data } = await api.editChecklist(params);

    return dispatch({
      type: CHECKLISTS_ACTION_TYPE.REORDER_CHECKLIST,
      payload: data,
    });
  },
);

export const createChecklistCategoryAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const api = resolveChecklistsApi(selectActiveChecklistTargetType(getState()));

    const { data } = await api.createChecklistCategory(params);

    return dispatch({
      type: CHECKLISTS_ACTION_TYPE.CREATE_CHECKLIST_CATEGORY,
      payload: data,
    });
  },
);

export const editChecklistCategoryAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const api = resolveChecklistsApi(selectActiveChecklistTargetType(getState()));

    const { data } = await api.editChecklist(params);

    return dispatch({
      type: CHECKLISTS_ACTION_TYPE.EDIT_CHECKLIST_CATEGORY,
      payload: data,
    });
  },
);

export const deleteChecklistCategoryAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const api = resolveChecklistsApi(selectActiveChecklistTargetType(getState()));

    await api.deleteChecklistCategory(params);

    return dispatch({
      type: CHECKLISTS_ACTION_TYPE.DELETE_CHECKLIST_CATEGORY,
      meta: params,
    });
  },
);

export const createChecklistItemAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const api = resolveChecklistsApi(selectActiveChecklistTargetType(getState()));

    const { data } = await api.createChecklistItem(params);

    return dispatch({
      type: CHECKLISTS_ACTION_TYPE.CREATE_CHECKLIST_ITEM,
      payload: data,
    });
  },
);

export const editChecklistItemAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const api = resolveChecklistsApi(selectActiveChecklistTargetType(getState()));

    const { data } = await api.editChecklist(params);

    return dispatch({
      type: CHECKLISTS_ACTION_TYPE.EDIT_CHECKLIST_ITEM,
      payload: data,
    });
  },
);

export const deleteChecklistItemAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const api = resolveChecklistsApi(selectActiveChecklistTargetType(getState()));

    await api.deleteChecklistItem(params);

    return dispatch({
      type: CHECKLISTS_ACTION_TYPE.DELETE_CHECKLIST_ITEM,
      meta: params,
    });
  },
);

export const disableGlobalChecklistAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const api = resolveChecklistsApi(selectActiveChecklistTargetType(getState()));

    await api.disableGlobalChecklist(params);

    return dispatch({
      type: CHECKLISTS_ACTION_TYPE.DISABLE_GLOBAL_CHECKLIST,
      meta: params,
    });
  },
);

export const enableGlobalChecklistAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const api = resolveChecklistsApi(selectActiveChecklistTargetType(getState()));

    await api.enableGlobalChecklist(params);

    return dispatch({
      type: CHECKLISTS_ACTION_TYPE.ENABLE_GLOBAL_CHECKLIST,
      meta: params,
    });
  },
);

export const cloneGlobalChecklistAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const api = resolveChecklistsApi(selectActiveChecklistTargetType(getState()));

    await api.cloneGlobalChecklist(params);

    return dispatch({
      type: CHECKLISTS_ACTION_TYPE.CLONE_GLOBAL_CHECKLIST,
      meta: params,
    });
  },
);

export const disableGlobalChecklistNodeAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const api = resolveChecklistsApi(selectActiveChecklistTargetType(getState()));

    await api.disableGlobalChecklistNode(params);

    return dispatch({
      type: CHECKLISTS_ACTION_TYPE.DISABLE_GLOBAL_CHECKLIST_NODE,
      meta: params,
    });
  },
);

export const enableGlobalChecklistNodeAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const api = resolveChecklistsApi(selectActiveChecklistTargetType(getState()));

    await api.enableGlobalChecklistNode(params);

    return dispatch({
      type: CHECKLISTS_ACTION_TYPE.ENABLE_GLOBAL_CHECKLIST_NODE,
      meta: params,
    });
  },
);

export const setChecklistVisibilityOverrideAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const api = resolveChecklistsApi(selectActiveChecklistTargetType(getState()));

    await api.setChecklistVisibilityOverride(params);

    return dispatch({
      type: CHECKLISTS_ACTION_TYPE.SET_VISIBILITY_OVERRIDE,
      meta: params,
    });
  },
);

export const removeChecklistVisibilityOverrideAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const api = resolveChecklistsApi(selectActiveChecklistTargetType(getState()));

    await api.removeChecklistVisibilityOverride(params);

    return dispatch({
      type: CHECKLISTS_ACTION_TYPE.REMOVE_VISIBILITY_OVERRIDE,
      meta: params,
    });
  },
);
